import { motion } from "framer-motion";
import { Map, CheckCircle2, Flame, Circle } from "lucide-react";
import { ROADMAP } from "@/data/site";

const statusStyle = {
  done: { dot: "bg-green-400 border-green-400", icon: CheckCircle2, iconColor: "text-green-400", badge: "text-green-400 border-green-400/40" },
  active: { dot: "bg-ember border-ember animate-pulse", icon: Flame, iconColor: "text-ember", badge: "text-ember border-ember/40" },
  upcoming: { dot: "bg-transparent border-gray-600", icon: Circle, iconColor: "text-gray-500", badge: "text-gray-400 border-white/10" },
};

const labelMap = { done: "Completed", active: "In Progress", upcoming: "Upcoming" };

export const Roadmap = () => {
  return (
    <section
      id="roadmap"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="roadmap-section"
    >
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blood/10 blur-[130px] rounded-full -z-0" />
      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 text-sm font-body font-bold tracking-widest uppercase text-gold mb-4">
            <Map className="w-4 h-4" /> The Path
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase text-white leading-[0.95]">
            Road To <span className="fire-gradient-text">Domination</span>
          </h2>
        </motion.div>

        <div className="relative">
          <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold via-ember to-blood/30 md:-translate-x-1/2" />

          <div className="space-y-10">
            {ROADMAP.map((item, i) => {
              const s = statusStyle[item.status];
              const Icon = s.icon;
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5 }}
                  className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-10 ${
                    isLeft ? "" : "md:[direction:rtl]"
                  }`}
                  data-testid={`roadmap-phase-${i}`}
                >
                  <div className={`absolute left-5 md:left-1/2 top-2 w-4 h-4 rounded-full border-2 ${s.dot} md:-translate-x-1/2 z-10`} />

                  <div className={`glass rounded-lg p-6 [direction:ltr] ${isLeft ? "md:text-right" : ""}`}>
                    <div className={`flex items-center gap-2 mb-2 ${isLeft ? "md:justify-end" : ""}`}>
                      <Icon className={`w-4 h-4 ${s.iconColor}`} />
                      <span className={`text-[10px] font-body font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${s.badge}`}>
                        {labelMap[item.status]}
                      </span>
                    </div>
                    <p className="font-body text-xs font-semibold uppercase tracking-widest text-gray-500">
                      {item.phase}
                    </p>
                    <h3 className="font-display text-2xl uppercase text-white mt-1">
                      {item.title}
                    </h3>
                    <ul className={`mt-4 space-y-2 ${isLeft ? "md:items-end" : ""} flex flex-col`}>
                      {item.items.map((li, j) => (
                        <li key={j} className="font-body text-sm text-gray-400 flex items-center gap-2">
                          <Flame className="w-3 h-3 text-ember/60 shrink-0" /> {li}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
